import React from "react";

import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import Tag from "antd/es/tag";
import { defineMessages, useIntl } from "react-intl";

import EmailStatus from "@mapmycustomers/shared/enum/EmailStatus";
import { Activity } from "@mapmycustomers/shared/types/entity";

import Section from "@app/component/activity/ActivityAnnotation/Section";
import getActivityEmailRecipients from "@app/util/activity/getActivityEmailRecipients";

import styles from "./RecipientsSection.module.scss";

const messages = defineMessages({
  bounced: {
    id: "component.activityAnnotation.recipientsSection.bounced",
    defaultMessage: "Bounced",
    description: "Badge shown next to an email recipient whose message bounced",
  },
  notDelivered: {
    id: "component.activityAnnotation.recipientsSection.notDelivered",
    defaultMessage: "Not delivered",
    description: "Badge for a recipient whose send errored or was cancelled (not a bounce)",
  },
  title: {
    id: "component.activityAnnotation.recipientsSection.title",
    defaultMessage: "Recipients ({count})",
    description: "Recipients section title, showing the total recipient count",
  },
  unknownRecipient: {
    id: "component.activityAnnotation.recipientsSection.unknownRecipient",
    defaultMessage: "Unknown recipient",
    description: "Fallback label for an email log that has neither a name nor an email",
  },
});

interface RecipientsSectionProps {
  activity: Activity;
}

// Renders the per-recipient delivery status of a multi-contact email activity
// from `emailLogs[]`. Bounced recipients appear here even after being delinked
// from `contactId[]`, since the list is driven by the logs (see
// getActivityEmailRecipients). Only rendered for genuinely multi-recipient
// emails — the BE collapses `emailLogs` to `[emailLog]` for a single recipient,
// so a one-row section would just duplicate the Relationships section.
const RecipientsSection = ({ activity }: RecipientsSectionProps) => {
  const intl = useIntl();
  const recipients = getActivityEmailRecipients(activity);

  if (recipients.length <= 1) {
    return null;
  }

  return (
    <Section
      icon={faUsers}
      title={intl.formatMessage(messages.title, { count: recipients.length })}
    >
      {recipients.map((recipient) => (
        <div className={styles.recipient} key={recipient.id}>
          <span className={styles.name}>
            {recipient.name || intl.formatMessage(messages.unknownRecipient)}
          </span>
          {recipient.hasBounced ? (
            <Tag className={styles.statusTag} color="error">
              {intl.formatMessage(messages.bounced)}
            </Tag>
          ) : recipient.status === EmailStatus.ERRORED ||
            recipient.status === EmailStatus.CANCELLED ? (
            <Tag className={styles.statusTag} color="warning">
              {intl.formatMessage(messages.notDelivered)}
            </Tag>
          ) : null}
        </div>
      ))}
    </Section>
  );
};

export default RecipientsSection;
